import { useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { IconCaretDownFilled, IconCheckFilled, IconDatabaseFilled } from "@tabler/icons-react";
import { SvgIcon, type TablerIcon } from "./icons.tsx";

/* ──────────────────────────────────────────────────────────────────────────
   The pieces of the Uploader pages the upload system never had.

   The queue, ledger, flows, duplicate and health pages were the
   Google Sheet's job, so there is no original to copy. They are built by
   these rules (DECISIONS.md, 2026-09-29) so they read as the same app:

   1. The page is the stats tab's page: .up-stats-page, its toolbar (a gold
      icon mark, a 22px title, a muted line) and its actions.
   2. Figures are its stat tiles; panels are its message and breakdown
      panels, with their section heads; lists are its category cards and
      creator rows; errors and empty states are its own.
   3. Controls are the editor's: the view tabs as a segmented filter, the
      gold Generate button and the Copy button, the dialog's inputs and
      fields, the manual duplication checkboxes, the creator menu's rows,
      the status pill of the worker connection dialog.
   4. Where nothing fits (a table's columns, a two column layout), a new
      up- class repeats the declarations of the original rule it follows
      and uses only the original's variables. They are listed in look.css
      after the lifted block.
   ────────────────────────────────────────────────────────────────────────── */

export function Page({ icon, title, sub, actions, children }: { icon: TablerIcon; title: string; sub?: ReactNode; actions?: ReactNode; children: ReactNode }) {
  return (
    <section className="up-stats-page">
      <div className="up-stats-toolbar">
        <div className="up-stats-title">
          <span className="up-stats-title-mark"><SvgIcon icon={icon} size={20} /></span>
          <div>
            <h1>{title}</h1>
            <p>{sub}</p>
          </div>
        </div>
        {actions ? <div className="up-stats-actions">{actions}</div> : null}
      </div>
      {children}
    </section>
  );
}

export function Tiles({ tiles }: { tiles: { label: string; value: ReactNode; tone?: "bad" }[] }) {
  return (
    <div className="up-stats-metrics">
      {tiles.map((t) => (
        <div key={t.label} className={`up-stat-tile${t.tone === "bad" ? " up-bad" : ""}`}>
          <span>{t.label}</span>
          <strong>{t.value}</strong>
        </div>
      ))}
    </div>
  );
}

export function Panel({ label, title, actions, foot, children, className = "" }: {
  label: string;
  title: ReactNode;
  actions?: ReactNode;
  foot?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`up-panel ${className}`.trim()} aria-label={label}>
      <div className="up-stats-section-head">
        <div>
          <span>{label}</span>
          <strong>{title}</strong>
        </div>
        {actions ? <div className="up-panel-actions">{actions}</div> : null}
      </div>
      {children}
      {foot ? <div className="up-panel-foot">{foot}</div> : null}
    </section>
  );
}

export function ErrorBar({ text, action }: { text: string; action?: { label: string; run: () => void } }) {
  return (
    <div className="up-stats-error" role="alert">
      <span>{text}</span>
      {action ? <button onClick={action.run}>{action.label}</button> : null}
    </div>
  );
}

export function Empty({ icon = IconDatabaseFilled, text }: { icon?: TablerIcon; text: ReactNode }) {
  return (
    <div className="up-stats-empty">
      <SvgIcon icon={icon} size={24} />
      <span>{text}</span>
    </div>
  );
}

export type PillTone = "ok" | "bad" | "warn" | "idle" | "run";

export function StatusPill({ tone, children }: { tone: PillTone; children: ReactNode }) {
  return <span className={`up-statuspill up-${tone}`}>{children}</span>;
}

/** A segmented filter drawn as the editor's view tabs. */
export function Segments<T extends string>({ label, value, options, onChange }: { label: string; value: T; options: { value: T; label: string }[]; onChange: (value: T) => void }) {
  return (
    <div className="up-seg up-view-seg" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} role="radio" aria-checked={o.value === value} className={`up-view-tab ${o.value === value ? "up-active" : ""}`} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * A choice from a short list, drawn as the editor's creator menu: the same
 * trigger (in a field's box), the same rows, the same tick.
 */
export function MenuSelect({ label, value, options, placeholder, onChange }: { label: string; value: string; options: { value: string; label: string }[]; placeholder: string; onChange: (value: string) => void }) {
  const [open, setOpen] = useState(false);
  const current = options.find((o) => o.value === value);
  return (
    <div className="up-cdrop up-menu-select">
      <button type="button" className="up-cdrop-trigger up-input" aria-haspopup="listbox" aria-expanded={open} aria-label={label} onClick={() => setOpen((o) => !o)}>
        <span className="up-cdrop-current">{current ? current.label : placeholder}</span>
        <SvgIcon icon={IconCaretDownFilled} className={`up-cdrop-chev ${open ? "up-up" : ""}`} size={14} />
      </button>
      {open ? (
        <>
          <div className="up-cdrop-backdrop" onClick={() => setOpen(false)}></div>
          <div className="up-cdrop-menu" role="listbox" aria-label={label}>
            {[{ value: "", label: placeholder }, ...options].map((o) => (
              <div
                key={o.value}
                role="option"
                aria-selected={o.value === value}
                className={`up-cdrop-item ${o.value === value ? "up-active" : ""}`}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
              >
                <span className="up-cdrop-name">{o.label}</span>
                {o.value === value ? <SvgIcon icon={IconCheckFilled} className="up-cdrop-tick" size={14} /> : null}
              </div>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

/** A link that leaves Signet: the sheet, a Drive folder. Always a new tab. */
export function OutLink({ href, children, className = "up-out" }: { href: string | null | undefined; children: ReactNode; className?: string }) {
  if (!href) return <>{children}</>;
  return (
    <a href={href} target="_blank" rel="noreferrer noopener" className={className}>
      {children}
    </a>
  );
}

/**
 * A link to an Uploader route. Module routes are built from the manifest at
 * runtime, so the router's typed path list only knows the core pages; the
 * cast lives here once instead of at every link.
 */
export function ULink({ to, search, className, children }: { to: string; search?: Record<string, string | undefined>; className?: string; children: ReactNode }) {
  return (
    <Link to={to as never} search={search as never} className={className}>
      {children}
    </Link>
  );
}
